import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePageMeta } from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import ShotPlaceholder from '@/components/marketing/ShotPlaceholder';
import brokerPrivateBid from '@/assets/broker-platform-polished.webp.asset.json';
import awardedLoad from '@/assets/awarded-load.jpg.asset.json';
import dispatchSheet from '@/assets/dispatch-sheet.jpg.asset.json';
import podUpload from '@/assets/pod-upload.jpg.asset.json';
import fullProductShot from '@/assets/lanerunner-2.webp.asset.json';
import lrDirectBest from '@/assets/lrdirect-the-best.webp.asset.json';
import loadboardShot from '@/assets/loadboard-2.webp.asset.json';
import carrierVerifiedShot from '@/assets/carrier-verified.webp.asset.json';
import modernBoardShot from '@/assets/modern-board.webp.asset.json';
import brokersFullPageMockup from '@/assets/chatgpt-image-jul-31-2026-02-08-51-pm.webp.asset.json';
import BrokerPlanGuidanceBanner from '@/components/marketing/BrokerPlanGuidanceBanner';
import SecondMonthFreePromo from '@/components/marketing/SecondMonthFreePromo';

import { supabase } from '@/integrations/supabase/client';
import { displayCompanyName } from '@/lib/displayCompanyName';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ArrowRight, Check, X, Crown, Network, MessageSquare, Smartphone,
  Shield, Gauge, TrendingUp, Target, BarChart3, Zap, Map, FileText,
  Truck, Clock, PhoneCall, Mail, Voicemail, MessagesSquare, Layers,
  Star, Radar, Sparkles, Gift,
} from 'lucide-react';

const TEAL = '#02857E';
const YELLOW = '#F7CF0D';
const INK = '#04211F';


type Cycle = 'monthly' | 'yearly';

const BrokersPage: React.FC = () => {
  // Invited brokers arrive here from the shipper's LR Direct invite email.
  // Stash the token (AuthPage falls back to this key after the Stripe
  // redirect) and forward it on every downstream CTA.
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [inviteCompany, setInviteCompany] = React.useState<string | null>(null);
  const [cycle, setCycle] = React.useState<Cycle>('monthly');

  React.useEffect(() => {
    if (inviteToken) {
      try { sessionStorage.setItem('lr_pending_network_invite_token', inviteToken); } catch { /* ignore */ }
    }
  }, [inviteToken]);

  // Look up who invited them so the banner can name the shipper company.
  React.useEffect(() => {
    let cancelled = false;
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await (supabase as any).rpc('preview_network_invite', { _token: inviteToken });
        const row = Array.isArray(data) ? data[0] : data;
        if (!cancelled && row?.shipper_company_name) {
          setInviteCompany(displayCompanyName(row.shipper_company_name));
        }
      } catch { /* banner falls back to generic copy */ }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  const withInvite = React.useCallback((path: string) => {
    if (!inviteToken) return path;
    return `${path}${path.includes('?') ? '&' : '?'}invite=${encodeURIComponent(inviteToken)}`;
  }, [inviteToken]);

  usePageMeta({
    fullTitle: true,
    title: 'For Brokers | LR Direct + Load Board | LaneRunner',
    description:
      'Keep the shippers you already have, in one place. Bid on their private freight, dispatch and update loads live, and work the full LaneRunner load board. No margin taken.',
  });

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const brokerPlans = getPlans(cycle);


  return (
    <div className="min-h-screen bg-white">
      <LandingHeader activePage="brokers" />

      {/* ══════════ 1. HERO ══════════ */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-br from-[#f0f7f6] via-white to-[#f5faf9] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(${TEAL} 1px, transparent 1px), linear-gradient(90deg, ${TEAL} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl" style={{ background: `${TEAL}1A` }} />

        <div className="relative max-w-6xl mx-auto">
          {inviteToken && (
            <div className="mb-7 rounded-xl border border-[#02857E]/30 bg-[#f1faf9] px-4 py-3.5 text-sm text-[#0a3d3a] max-w-3xl">
              <strong>
                You've been invited by {inviteCompany || 'a shipper'} to their private broker network.
              </strong>{' '}
              Your invite is saved. Read what LR Direct is, then pick a plan when you're ready.
            </div>
          )}

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#02857E]/30 text-[#02857E] text-xs font-semibold tracking-wider mb-5">
                <Network className="w-3.5 h-3.5" /> LR DIRECT · FOR BROKERS
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5">
                Keep the shippers you already have.
                <br />
                <span style={{ color: TEAL }}>In one place.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-7 max-w-2xl">
                Your shippers are moving their freight onto LaneRunner and it's becoming how they
                work. LR Direct keeps you working with them the way you always have, just organized
                and updated live. No more digging through scattered texts, calls, and email chains to
                find out what happened on a load.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full text-white font-semibold px-7 shadow-lg hover:opacity-90"
                  style={{ backgroundColor: TEAL }}
                >
                  <Link to={withInvite('/pricing?role=broker')}>
                    See Plans &amp; Join <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full border-[#02857E]/40 text-[#02857E] hover:bg-[#02857E]/10 font-semibold px-7"
                >
                  <a href="#how-it-works" onClick={scrollTo('how-it-works')}>How it works</a>
                </Button>
              </div>

              <p className="text-sm text-gray-700 font-medium">
                No margin taken on your freight. The shipper relationship stays yours.
              </p>
            </div>

            <div className="lg:col-span-2">
              <div className="w-full aspect-[4/3] flex items-center">
                <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-black">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/YTvMHhhbpWs?rel=0&modestbranding=1"
                    title="LR Direct demo video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ 2. THE PROBLEM ══════════ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0c1420] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 25% 20%, white 1px, transparent 1px)',
            backgroundSize: '34px 34px',
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Right now your shipper freight lives everywhere.
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              One load, five places. That's how a pickup change gets missed and how a POD request
              turns into three phone calls.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* LEFT: chaos */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-7">
              <div className="flex items-center gap-2 mb-5">
                <span className="px-2.5 py-1 rounded-full bg-red-500/15 text-red-300 text-xs font-bold tracking-wider">
                  TODAY
                </span>
                <span className="text-white/60 text-sm">Scattered across five apps</span>
              </div>
              <ul className="space-y-3">
                {[
                  { icon: MessagesSquare, t: '6:04am text', b: '"Got a load Dallas to Memphis, can you cover it?" No rate, no times.' },
                  { icon: Mail, t: 'Email chain, 9 replies deep', b: 'The rate con is attached somewhere in the middle of it.' },
                  { icon: Voicemail, t: 'Voicemail from the plant', b: 'Pickup moved to 2pm. Nobody else on the load knows yet.' },
                  { icon: PhoneCall, t: 'Call at 4:30pm', b: '"Any update?" You call the driver, then call back.' },
                  { icon: FileText, t: 'Text the next morning', b: '"Did it deliver? Shipper wants the POD." You go hunting for it.' },
                ].map((r) => (
                  <li key={r.t} className="flex gap-3 rounded-xl bg-white/[0.03] border border-white/5 p-3.5">
                    <r.icon className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-300/80" />
                    <div>
                      <div className="text-white font-semibold text-sm">{r.t}</div>
                      <div className="text-white/60 text-sm leading-relaxed">{r.b}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT: organized */}
            <div className="rounded-2xl border p-6 sm:p-7" style={{ borderColor: `${TEAL}66`, background: `${TEAL}14` }}>
              <div className="flex items-center gap-2 mb-5">
                <span className="px-2.5 py-1 rounded-full text-xs font-bold tracking-wider text-white" style={{ backgroundColor: TEAL }}>
                  WITH LR DIRECT
                </span>
                <span className="text-white/70 text-sm">One load thread</span>
              </div>
              <ul className="space-y-3">
                {[
                  { icon: Clock, t: 'Posted, bid, awarded', b: 'Every step timestamped on the load. You can see exactly when it happened and who did it.' },
                  { icon: FileText, t: 'Rate, stops, and times live on the load', b: 'The shipper changes a pickup window and it changes on your screen. No voicemail relay.' },
                  { icon: Truck, t: 'Dispatch, tracking, and docs attached', b: 'Carrier info, tracking link, and paperwork sit on the same record as the bid you won.' },
                  { icon: Check, t: 'POD uploaded, shipper sees it instantly', b: 'You upload once. The shipper gets it without asking. The load closes itself out.' },
                ].map((r) => (
                  <li key={r.t} className="flex gap-3 rounded-xl bg-black/20 border border-white/10 p-3.5">
                    <r.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: YELLOW }} />
                    <div>
                      <div className="text-white font-semibold text-sm">{r.t}</div>
                      <div className="text-white/70 text-sm leading-relaxed">{r.b}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-4xl mx-auto text-center mt-10">
            Most brokers lose hours a day to that back and forth, and it's where the mistakes live.
            A missed appointment change, a rate con nobody can find, a POD sitting in a photo roll.
            LR Direct puts you and your shipper on the same live record, so the update happens once
            and you both have it.
          </p>
        </div>
      </section>

      {/* ══════════ 6. OPEN MARKETPLACE — LR DIRECT FULL ══════════ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: YELLOW }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-900 text-white text-xs font-semibold tracking-wider mb-5">
              <Crown className="w-3.5 h-3.5" /> LANERUNNER · $99.99/MO
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Want new shippers too?
            </h2>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-5">
              We bring the freight. You win the relationship.
            </p>
            <div className="space-y-4 text-gray-900/85 text-base sm:text-lg leading-relaxed">
              <p>
                Full access opens the open LaneRunner marketplace. Real shippers posting real freight
                and looking for brokers to cover it, not a directory of leads you have to cold call.
              </p>
              <p>
                Win their loads, do the job right, and the relationship becomes yours. Most shippers
                who start on the open market end up adding the brokers they like to their private
                network, which means you go from bidding to first look. We take no margin on any of
                it, ever.
              </p>
              <p className="font-semibold">
                Full includes everything in LR Direct Private, plus the full load board.
              </p>
            </div>
            <div className="mt-7">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-gray-900 text-white hover:bg-gray-800 font-semibold px-8"
              >
                <Link to={withInvite('/auth?mode=signup&role=broker&tier=lr_direct_private')}>
                  Get LaneRunner Now <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden">
            <img
              src={brokersFullPageMockup.url}
              alt="Open LR Direct marketplace with shipper loads open for broker bidding"
              className="w-full h-full object-cover object-center"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ══════════ 3. HOW LR DIRECT WORKS ══════════ */}
      <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Layers className="w-3.5 h-3.5" /> HOW IT WORKS
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Five steps, start to POD
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              This is the whole mechanic. Nothing hidden, no middleman step where somebody else
              touches your freight.
            </p>
          </div>

          <div className="space-y-8 sm:space-y-10">
            {[
              {
                n: '01',
                title: 'Your shipper posts to their private network',
                body: 'The shipper builds a network of the brokers they actually trust and posts the load to that group. If they invited you, you are in that group. The freight never sits on a public board first.',
                shot: 'Shipper private network load post',
              },
              {
                n: '02',
                title: 'You get first look and bid directly',
                body: 'You see the lane, the stops, the equipment, and the dates, and you put your number in. It is a small vetted pool bidding, not a hundred callers hammering one phone line. Tight competition, real freight.',
                shot: 'Broker bid entry on a private load',
              },
              {
                n: '03',
                title: 'The shipper awards the load',
                body: 'They pick a broker and the load is yours. You win the freight and you keep the direct relationship with that shipper. LaneRunner takes no margin on the load, ever.',
                shot: 'Award confirmation and won load',
              },
              {
                n: '04',
                title: 'You dispatch, attach docs, and update status',
                body: 'Add the carrier, attach the rate con and paperwork, drop in a tracking link, and move the load through pickup and transit right on the record. Your shipper watches it live instead of calling you for it.',
                shot: 'Dispatch sheet with status tracker',
              },
              {
                n: '05',
                title: 'POD uploads and the load closes',
                body: 'Upload the POD and the shipper has it the second it lands. The load closes with the full history attached, so when they ask about it in three months the answer is already there. Next load runs the same clean way.',
                shot: 'POD upload and closed load history',
              },
            ].map((s, idx) => (
              <div
                key={s.n}
                className={`grid lg:grid-cols-2 gap-6 lg:gap-12 items-center ${idx % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}
              >
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: TEAL }}
                    >
                      {s.n}
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{s.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-base sm:text-lg">{s.body}</p>
                </div>
                {s.n === '01' ? (
                  <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-2 sm:p-3">
                    <img
                      src={fullProductShot.url}
                      alt="LR Direct load record with bid, dispatch, tracking, and docs"
                      className="w-full h-full object-contain object-center"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : s.n === '02' ? (
                  <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-gray-200 bg-white flex items-center justify-center">
                    <img
                      src={brokerPrivateBid.url}
                      alt="Broker bid entry on a private load"
                      className="w-full h-full object-contain object-center"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : s.n === '03' ? (
                  <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-2 sm:p-3">
                    <img
                      src={awardedLoad.url}
                      alt="Award confirmation on a won load"
                      className="w-full h-full object-contain object-center"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : s.n === '04' ? (
                  <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-2 sm:p-3">
                    <img
                      src={dispatchSheet.url}
                      alt="Dispatch sheet with status tracker"
                      className="w-full h-full object-contain object-center"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : s.n === '05' ? (
                  <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-2 sm:p-3">
                    <img
                      src={podUpload.url}
                      alt="POD upload and closed load history"
                      className="w-full h-full object-contain object-center"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : (
                  <ShotPlaceholder label={s.shot} hint={`Step ${s.n}`} ratio="aspect-[16/10]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ 4. WHAT YOU GET — LR DIRECT PRIVATE ══════════ */}
      <section
        className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #026d67 100%)` }}
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}
        />
        <div className="relative max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-semibold tracking-wider mb-4">
                <Network className="w-3.5 h-3.5" /> LANERUNNER · $99.99/MO
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                The shippers you already work with, on rails.
              </h2>
              <p className="text-lg text-white/85 max-w-3xl mx-auto">
                Everything below comes with LaneRunner — the load board, private shipper networks, and the open marketplace. All for $99.99.
              </p>
            </div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: Star,
                title: 'First look at their freight',
                body: 'Loads hit their private broker network before they go anywhere near an open market. You bid early, against a short list, on freight from a shipper who already knows your name. The relationship you built is the advantage, not a tiebreaker.',
              },
              {
                icon: Layers,
                title: 'Everything on one load',
                body: 'The bid, the award, the dispatch, the tracking link, and the documents all live on the same load record. No cross-referencing an email against a text against a spreadsheet. Open the load and the whole story is there.',
              },
              {
                icon: Radar,
                title: 'Your shipper stays updated automatically',
                body: 'Status changes, pickup and delivery times, and the POD show up on their screen as you enter them. You stop being the middleman on every "any update?" call and get that time back for actual work.',
              },
              {
                icon: MessageSquare,
                title: 'Direct messaging on awarded loads',
                body: 'Message the shipper inside the load you won, so the conversation is attached to the freight it belongs to. No hunting through a phone thread six weeks later to prove what was agreed.',
              },
              {
                icon: Smartphone,
                title: 'On your phone, anywhere',
                body: 'The full platform is in the LaneRunner app. Bid from a dock, update a status from the yard, upload a POD from the truck stop. What you do on the phone is what your shipper sees on their desktop.',
              },
              {
                icon: Zap,
                title: 'Ad-free, boost credits, team seats',
                body: 'Clean workspace with no ad slots, monthly boost credits to push your own postings up the board, and seats so your team works out of the same account instead of forwarding each other screenshots.',
              },
            ].map((c) => (
              <div key={c.title} className="bg-white rounded-2xl p-6 sm:p-7 shadow-xl">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${TEAL}15` }}>
                  <c.icon className="w-6 h-6" style={{ color: TEAL }} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-gray-600 leading-relaxed text-[15px]">{c.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <div className="w-full aspect-[3/2] rounded-2xl overflow-hidden">
              <img
                src={lrDirectBest.url}
                alt="LR Direct load record: bid, dispatch, tracking, and docs"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ 5. THE LOAD BOARD SIDE ══════════ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Truck className="w-3.5 h-3.5" /> INCLUDED IN EVERY BROKER PLAN
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              And you still get the full load board
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              LR Direct is the shipper side. This is the day-to-day: posting your freight, covering
              it with carriers you can check out first, and knowing what a lane actually pays.
            </p>
          </div>

          {/* 5a. Post loads fast */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-16">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Post loads in seconds, not minutes
              </h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                The posting form is built for someone doing this forty times a day, not once a month.
                It fills itself in from your previous loads, maps your stops as you add them, and
                remembers the lanes you run every week so a recurring load is two clicks.
              </p>
              <ul className="space-y-2.5">
                {[
                  'Auto-fill from previous loads so you retype almost nothing',
                  'Multi-stop routing drawn on a map as you build the load',
                  'Save templates for lanes you run on repeat',
                  'Bulk post up to 100 loads from a spreadsheet',
                  'Voice posting: say the load out loud, confirm the details it pulls',
                ].map((t) => (
                  <li key={t} className="flex gap-2 text-gray-800">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full aspect-[1717/916] rounded-2xl overflow-hidden">
              <img
                src={loadboardShot.url}
                alt="Post a load form with multi-stop map"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* 5b. Verified carriers */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-16">
            <div className="lg:order-1 rounded-2xl overflow-hidden aspect-[16/9]">
              <img
                src={carrierVerifiedShot.url}
                alt="Carrier detail: FMCSA authority, insurance on file, broker reviews and on-time delivery"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-700 text-xs font-semibold tracking-wider mb-4">
                <Shield className="w-3.5 h-3.5" style={{ color: TEAL }} /> VERIFIED CARRIERS
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Know who you're booking
              </h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Every carrier is checked against FMCSA before they can work on the board, and the
                record is on their profile where you can read it in ten seconds. You should not have
                to pull three websites to decide whether to give somebody your customer's freight.
              </p>
              <ul className="space-y-2.5">
                {[
                  'FMCSA safety rating and operating authority status',
                  'Insurance on file and checked, not just claimed',
                  'Reviews from other brokers who booked them',
                  'On-time delivery percentage across their history',
                  'Build a private favorites list of the carriers you trust',
                ].map((t) => (
                  <li key={t} className="flex gap-2 text-gray-800">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 5c. The whole market */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-700 text-xs font-semibold tracking-wider mb-4">
                <Map className="w-3.5 h-3.5" style={{ color: TEAL }} /> MODERN BOARD
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                The whole market, one modern board
              </h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                The board updates live, so what you are looking at is what is actually out there right
                now. Every lane carries the real spot rate next to it, every result shows route miles
                and drive time, and every posting broker shows a credit score and days to pay before
                you commit.
              </p>
              <ul className="space-y-2.5">
                {[
                  'Live board that updates as loads post and clear',
                  'Real spot market rates on every lane you search',
                  'Saved searches and backhaul suggestions off your current load',
                  'Route maps with mileage and drive time',
                  'Broker credit scores and average days to pay',
                ].map((t) => (
                  <li key={t} className="flex gap-2 text-gray-800">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[3/2]">
              <img
                src={modernBoardShot.url}
                alt="Search loads board with spot rates, route map and carrier verification"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* 5d. ATLAS */}
          <div className="rounded-3xl bg-white border border-gray-200 p-6 sm:p-10">
            <div className="max-w-3xl mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5" /> ATLAS
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                The numbers behind the number you quote
              </h3>
              <p className="text-gray-600 leading-relaxed">
                ATLAS is the rate and market tooling built into the board. It reads the same freight
                data you are working in and tells you what a lane pays, where it has been heading, and
                how tight it is today. Plain answers you can use on a call.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Gauge, t: 'Rate Check', b: 'Type a lane, get the benchmark range for it before you quote. Low, average, and high, based on what that lane is actually moving at.' },
                { icon: TrendingUp, t: 'Historical Rate Trends', b: 'See where the lane has been over the past weeks and months, so you know whether today\'s number is a spike or the new normal.' },
                { icon: Target, t: 'Lane Predictor', b: 'A forward read on where a lane is likely heading, so you can decide whether to hold your rate or move now.' },
                { icon: BarChart3, t: 'Pattern Detector & Repricing', b: 'Flags your own postings that are sitting too long or priced off the market, and shows what to reprice them to.' },
                { icon: Zap, t: 'Market Pressure Index', b: 'A live tightness read per lane. High pressure means capacity is short and you should move fast on a carrier.' },
                { icon: Crown, t: 'Command Center', b: 'Templates, bulk repricing, and posting optimization in one screen instead of scattered across the app.' },
              ].map((f) => (
                <div key={f.t} className="p-5 rounded-xl bg-gray-50 border border-gray-200">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${TEAL}15` }}>
                    <f.icon className="w-5 h-5" style={{ color: TEAL }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{f.t}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ══════════ 7. PRICING ══════════ */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Broker plans</h2>
            <p className="text-lg text-gray-600">
              Flat monthly subscription. No margin taken on your freight. Cancel anytime.
            </p>
          </div>

          {/* cycle toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-full bg-gray-100 p-1">
              {(['monthly', 'yearly'] as Cycle[]).map((c) => (
                <button
                  key={c}
                  onClick={() => setCycle(c)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${
                    cycle === c ? 'text-white shadow' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={cycle === c ? { backgroundColor: TEAL } : undefined}
                >
                  {c}
                  {c === 'yearly' && <span className="ml-1.5 text-[11px] opacity-90">save 20%</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Promo: second month free (copy only) */}
          <SecondMonthFreePromo className="mb-6" />

          {/* "Which plan do I need?" — answered before the cards. */}
          <BrokerPlanGuidanceBanner
            invited={!!inviteToken}
            shipperName={inviteCompany}
          />


          <div className="grid md:grid-cols-2 gap-6 items-start">
            {brokerPlans.map((p) => {
              const isYellow = p.id === 'full_access';
              return (
                <div
                  key={p.id}
                  className={`relative rounded-2xl p-7 transition-all hover:shadow-xl ${
                    isYellow ? 'shadow-xl mt-8 md:mt-0' : 'bg-white border-2'
                  } ${
                    isYellow && inviteToken
                      ? 'ring-4 ring-[#02857E]/40 ring-offset-2'
                      : ''
                  }`}
                  style={{
                    backgroundColor: isYellow ? YELLOW : '#ffffff',
                    borderColor: isYellow ? '#C9A30B' : '#e5e7eb',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                  }}
                >
                  {isYellow ? (
                    <Badge
                      className="absolute -top-3 left-1/2 -translate-x-1/2 font-bold tracking-wide px-3 py-1 text-center whitespace-nowrap"
                      style={{ backgroundColor: INK, color: YELLOW }}
                    >
                      REQUIRED FOR SHIPPER INVITATIONS
                    </Badge>
                  ) : null}
                  <h3
                    className="text-xl font-bold text-gray-900 mb-1 mt-1"
                    style={{ color: isYellow ? INK : undefined }}
                  >
                    {p.name}
                  </h3>
                  {isYellow && (
                    <span
                      className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.08em] whitespace-nowrap shadow-[0_2px_8px_-2px_rgba(1,74,70,0.45)] ring-1 ring-black/10 mb-2"
                      style={{ backgroundColor: INK, color: YELLOW }}
                    >
                      <Gift className="w-3 h-3" strokeWidth={2.6} style={{ color: YELLOW }} />
                      2nd month free — limited time
                    </span>
                  )}
                  <p
                    className="text-sm text-gray-600 mb-4 min-h-[40px]"
                    style={{ color: isYellow ? `${INK}CC` : undefined }}
                  >
                    {p.blurb}
                  </p>

                  <div className="mb-1">
                    <span
                      className="text-4xl font-bold text-gray-900"
                      style={{ color: isYellow ? INK : undefined }}
                    >
                      {p.price}
                    </span>
                    <span
                      className="text-gray-600"
                      style={{ color: isYellow ? `${INK}CC` : undefined }}
                    >
                      {p.suffix}
                    </span>
                  </div>
                  <p
                    className="text-xs text-gray-500 mb-5 min-h-[16px]"
                    style={{ color: isYellow ? `${INK}AA` : undefined }}
                  >
                    {p.note}
                  </p>

                  <ul className="space-y-2 text-sm mb-4">
                    {p.included.map((t) => (
                      <li
                        key={t}
                        className="flex gap-2 text-gray-800"
                        style={{ color: isYellow ? INK : undefined }}
                      >
                        <Check
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: isYellow ? INK : TEAL }}
                        />
                        {t}
                      </li>
                    ))}
                  </ul>
                  {p.excluded.length > 0 && (
                    <ul className="space-y-2 text-sm mb-6 pt-3 border-t border-gray-100">
                      {p.excluded.map((t) => (
                        <li key={t} className="flex gap-2 text-gray-400">
                          <X className="w-4 h-4 mt-0.5 flex-shrink-0" />{t}
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    asChild
                    className="w-full rounded-full font-semibold"
                    style={
                      isYellow
                        ? { backgroundColor: INK, color: YELLOW }
                        : { backgroundColor: TEAL, color: '#fff' }
                    }
                  >
                    <Link to={withInvite(p.href)}>
                      {p.cta}
                    </Link>
                  </Button>
                </div>
              );
            })}
          </div>


          <p className="text-center text-sm text-gray-600 mt-6">
            <Link to={withInvite('/pricing?role=broker')} className="text-[#02857E] font-semibold hover:underline">
              See the full plan comparison →
            </Link>
          </p>
        </div>
      </section>

      {/* ══════════ 8. FAQ ══════════ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 text-center">
            Questions brokers actually ask
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10">
            Straight answers. If yours isn't here, email support@lanerunner.com.
          </p>

          <Accordion type="single" collapsible className="space-y-3">
            {[
              {
                q: 'Do I have to pay to accept a shipper\'s invite?',
                a: 'You need LR Direct Private at $99.99 a month, or Full, to bid on private network freight. The invite itself is saved to your browser and applied when you sign up, so you can read this page first and pick a plan when you are ready. Nothing expires while you decide.',
              },
              {
                q: 'Does LaneRunner take a cut of my freight?',
                a: 'No. Zero margin on the load. You pay a flat monthly subscription and every dollar of the spread stays with you. We are not a broker and we do not sit between you and the shipper on the money.',
              },
              {
                q: 'Do I keep the shipper relationship?',
                a: 'Yes. LR Direct is the workspace, not a broker in the middle. You win the load, you talk to the shipper directly, and the relationship is yours to keep and grow.',
              },
              {
                q: 'What is the difference between Private and Full?',
                a: 'Private lets you bid on freight from shippers who invited you to their network. Full is that plus the open LR Direct marketplace, where shippers you have never worked with post loads and you can win new accounts.',
              },
              {
                q: 'Is there a load board too, or just LR Direct?',
                a: 'Both. Every broker plan includes the full modern load board: posting, searching, verified carriers, spot rates, route maps, and the ATLAS rate tooling. LR Direct is what sits on top of it for your shipper relationships.',
              },
              {
                q: 'Can I use it on my phone?',
                a: 'Yes. The full platform is in the LaneRunner app, so you can bid, dispatch, update a status, and upload a POD from anywhere. Google Play now.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes. Cancel from your billing settings and you keep access through the end of the period you already paid for. No contract and no cancellation call.',
              },
            ].map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="rounded-xl border border-gray-200 bg-white px-5"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ══════════ 9. FINAL CTA ══════════ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 relative overflow-hidden bg-[#0c1420]">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-5">
            {inviteToken
              ? 'Your shipper set this up for you. Two minutes to join.'
              : 'Two minutes to join.'}
          </h2>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Accept your invite, pick a plan, and start bidding on their freight the same day.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              size="lg"
              className="rounded-full text-white font-semibold px-8 hover:opacity-90"
              style={{ backgroundColor: TEAL }}
            >
              <Link to={withInvite('/auth?mode=signup&role=broker&tier=lr_direct_private')}>
                Accept &amp; Join <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-white/40 text-white hover:bg-white/10 font-semibold px-8"
            >
              <a href="#pricing" onClick={scrollTo('pricing')}>Compare plans</a>
            </Button>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

// ─── pricing data (mirrors the real billing tiers) ───
interface PlanCard {
  id: 'board' | 'full_access';
  name: string;
  blurb: string;
  price: string;
  suffix: string;
  note: string;
  included: string[];
  excluded: string[];
  cta: string;
  href: string;
}

function getPlans(cycle: Cycle): PlanCard[] {
  const yearly = cycle === 'yearly';
  return [
    {
      id: 'board',
      name: 'Load Board',
      blurb: 'The modern board on its own. No direct shipper bidding.',
      price: yearly ? '$575.90' : '$59.99',
      suffix: yearly ? '/year' : '/month',
      note: yearly ? '$47.99/mo billed yearly' : '',
      included: [
        'Full modern load board',
        'Post and search loads, unlimited',
        'Verified carriers with FMCSA records',
        'Broker credit scores and days to pay',
        'Route maps, mileage, and drive time',
      ],
      excluded: [
        'Private shipper networks',
        'Open marketplace',
      ],
      cta: 'Subscribe',
      href: yearly
        ? '/auth?mode=signup&role=broker&tier=board_only_yearly'
        : '/auth?mode=signup&role=broker&tier=board_only',
    },
    {
      id: 'full_access',
      name: 'Full Access',
      blurb: 'Private shipper networks, the open marketplace, and the full load board.',
      price: yearly ? '$959.90' : '$99.99',
      suffix: yearly ? '/year' : '/month',
      note: yearly ? '$79.99/mo billed yearly per user' : 'per user',
      included: [
        'Everything in Load Board',
        'Join your shippers\' private networks',
        'First look and direct bidding on their freight',
        'Bid on the open marketplace and win new shippers',
        'Dispatch, tracking, and docs on every load',
        'Direct messaging with your shippers',
        'Rate Check and historical rate trends',
        'gain valuable new shippers',
        '20 boost credits per month',
        'No margin taken, ever',
      ],
      excluded: [],
      cta: 'Activate Full Access',
      href: yearly
        ? '/auth?mode=signup&role=broker&tier=premium-yearly'
        : '/auth?mode=signup&role=broker&tier=lr_direct_private',
    },
  ];
}

export default BrokersPage;
